// @ts-strict-ignore
import { useMutation, useQuery } from "@apollo/client";
import ActionDialog from "@dashboard/components/ActionDialog";
import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { DashboardCard } from "@dashboard/components/Card";
import { ListPageLayout } from "@dashboard/components/Layouts";
import ResponsiveTable from "@dashboard/components/ResponsiveTable";
import TableCellHeader from "@dashboard/components/TableCellHeader";
import useNotifier from "@dashboard/hooks/useNotifier";
import {
  Chip,
  IconButton,
  Tab,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import VisibilityIcon from "@material-ui/icons/Visibility";
import { Box, Button, Skeleton, Text } from "@saleor/macaw-ui-next";
import React, { useState } from "react";

import { Select } from "@dashboard/components/Select";
import BannerDialog from "../components/BannerDialog";
import BannerPreviewDialog from "../components/BannerPreviewDialog";
import ShortDialog from "../components/ShortDialog";
import ShortPreviewDialog from "../components/ShortPreviewDialog";
import SlotDialog from "../components/SlotDialog";
import SlotPreviewDialog from "../components/SlotPreviewDialog";
import VacationDialog from "../components/VacationDialog";
import {
  CAROUSEL_BANNER_DELETE_MUTATION,
  CMS_DELIVERY_SLOT_DELETE_MUTATION,
  CMS_DELIVERY_VACATION_DELETE_MUTATION,
  SHORT_VIDEO_DELETE_MUTATION,
} from "../cmsMutations";
import {
  CAROUSEL_BANNERS_QUERY,
  CMS_DELIVERY_SLOTS_QUERY,
  CMS_DELIVERY_VACATIONS_QUERY,
  DELIVERY_LOCATIONS_QUERY,
  SEASONAL_PICKS_QUERY,
  SHORT_VIDEOS_QUERY,
} from "../cmsQueries";
import { CarouselBanner, CMSDeliverySlot, CMSDeliveryVacation, ShortVideo } from "../types";

interface DeleteTarget {
  id: string;
  name: string;
  type: "Banner" | "Short Video" | "Delivery Slot" | "Vacation Date";
}

const FILTER_PLATFORM_OPTIONS = [
  { label: "All Platforms", value: "ALL" },
  { label: "Mobile App", value: "APP" },
  { label: "Web App", value: "WEB" },
];

const FILTER_DEVICE_OPTIONS = [
  { label: "All Devices", value: "ALL" },
  { label: "Mobile", value: "MOBILE" },
  { label: "Tablet", value: "TABLET" },
  { label: "Desktop", value: "DESKTOP" },
];

const formatTargetingBadge = (platform?: string, device?: string) => {
  const p = platform === "APP" ? "Mobile App" : platform === "WEB" ? "Web App" : "All Platforms";
  const d = device === "MOBILE" ? "Mobile" : device === "TABLET" ? "Tablet" : device === "DESKTOP" ? "Desktop" : "All Devices";
  return `${p} • ${d}`;
};

export const CmsPage: React.FC = () => {
  const notify = useNotifier();
  const [tabIndex, setTabIndex] = useState<number>(0);

  // Platform & Device Filters
  const [bannerPlatformFilter, setBannerPlatformFilter] = useState<string>("ALL");
  const [bannerDeviceFilter, setBannerDeviceFilter] = useState<string>("ALL");
  const [shortPlatformFilter, setShortPlatformFilter] = useState<string>("ALL");
  const [shortDeviceFilter, setShortDeviceFilter] = useState<string>("ALL");

  // Dialog states
  const [bannerDialogOpen, setBannerDialogOpen] = useState(false);
  const [editingBanner, setEditingBanner] = useState<CarouselBanner | null>(null);
  const [previewBanner, setPreviewBanner] = useState<CarouselBanner | null>(null);

  const [shortDialogOpen, setShortDialogOpen] = useState(false);
  const [editingShort, setEditingShort] = useState<ShortVideo | null>(null);
  const [previewShort, setPreviewShort] = useState<ShortVideo | null>(null);

  const [slotDialogOpen, setSlotDialogOpen] = useState(false);
  const [editingSlot, setEditingSlot] = useState<CMSDeliverySlot | null>(null);
  const [previewSlot, setPreviewSlot] = useState<CMSDeliverySlot | null>(null);

  const [vacationDialogOpen, setVacationDialogOpen] = useState(false);

  // Delete Confirmation state
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);

  // Queries
  const { data: bannersData, loading: bannersLoading } = useQuery(CAROUSEL_BANNERS_QUERY, {
    variables: {
      platform: bannerPlatformFilter !== "ALL" ? bannerPlatformFilter : undefined,
      device: bannerDeviceFilter !== "ALL" ? bannerDeviceFilter : undefined,
    },
  });
  const { data: shortsData, loading: shortsLoading } = useQuery(SHORT_VIDEOS_QUERY, {
    variables: {
      platform: shortPlatformFilter !== "ALL" ? shortPlatformFilter : undefined,
      device: shortDeviceFilter !== "ALL" ? shortDeviceFilter : undefined,
    },
  });
  const { data: slotsData, loading: slotsLoading } = useQuery(CMS_DELIVERY_SLOTS_QUERY);
  const { data: vacationsData, loading: vacationsLoading } = useQuery(CMS_DELIVERY_VACATIONS_QUERY);
  const { data: picksData, loading: picksLoading } = useQuery(SEASONAL_PICKS_QUERY);
  const { data: locsData, loading: locsLoading } = useQuery(DELIVERY_LOCATIONS_QUERY);

  // Delete Mutations
  const [deleteBanner, { loading: deletingBanner }] = useMutation(CAROUSEL_BANNER_DELETE_MUTATION, {
    refetchQueries: [{ query: CAROUSEL_BANNERS_QUERY }],
    onCompleted: () => {
      notify({ status: "success", text: "Banner deleted" });
      setDeleteTarget(null);
    },
  });
  const [deleteShort, { loading: deletingShort }] = useMutation(SHORT_VIDEO_DELETE_MUTATION, {
    refetchQueries: [{ query: SHORT_VIDEOS_QUERY }],
    onCompleted: () => {
      notify({ status: "success", text: "Short video deleted" });
      setDeleteTarget(null);
    },
  });
  const [deleteSlot, { loading: deletingSlot }] = useMutation(CMS_DELIVERY_SLOT_DELETE_MUTATION, {
    refetchQueries: [{ query: CMS_DELIVERY_SLOTS_QUERY }],
    onCompleted: () => {
      notify({ status: "success", text: "Delivery slot deleted" });
      setDeleteTarget(null);
    },
  });
  const [deleteVacation, { loading: deletingVacation }] = useMutation(CMS_DELIVERY_VACATION_DELETE_MUTATION, {
    refetchQueries: [{ query: CMS_DELIVERY_VACATIONS_QUERY }],
    onCompleted: () => {
      notify({ status: "success", text: "Vacation date deleted" });
      setDeleteTarget(null);
    },
  });

  const handleConfirmDelete = () => {
    if (!deleteTarget) return;
    const { id, type } = deleteTarget;
    if (type === "Banner") deleteBanner({ variables: { id } });
    else if (type === "Short Video") deleteShort({ variables: { id } });
    else if (type === "Delivery Slot") deleteSlot({ variables: { id } });
    else if (type === "Vacation Date") deleteVacation({ variables: { id } });
  };

  const getPrimaryAction = () => {
    switch (tabIndex) {
      case 0:
        return (
          <Button
            onClick={() => {
              setEditingBanner(null);
              setBannerDialogOpen(true);
            }}
          >
            Add Carousel Banner
          </Button>
        );
      case 1:
        return (
          <Button
            onClick={() => {
              setEditingShort(null);
              setShortDialogOpen(true);
            }}
          >
            Add Short Video
          </Button>
        );
      case 2:
        return (
          <Button
            onClick={() => {
              setEditingSlot(null);
              setSlotDialogOpen(true);
            }}
          >
            Add Delivery Slot
          </Button>
        );
      case 3:
        return (
          <Button
            onClick={() => {
              setVacationDialogOpen(true);
            }}
          >
            Add Blackout Date
          </Button>
        );
      default:
        return null;
    }
  };

  return (
    <ListPageLayout>
      <TopNav title="Content Management System (CMS)">{getPrimaryAction()}</TopNav>

      <Box display="flex" flexDirection="column" gap={4}>
        <DashboardCard>
          <Tabs
            value={tabIndex}
            onChange={(_, val) => setTabIndex(val)}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab label="Carousel Banners" />
            <Tab label="Short Videos" />
            <Tab label="Delivery Slots" />
            <Tab label="Delivery Vacations" />
            <Tab label="Seasonal Picks" />
            <Tab label="Delivery Locations" />
          </Tabs>
        </DashboardCard>

        {/* ── TAB 0: BANNERS ── */}
        {tabIndex === 0 && (
          <DashboardCard>
            <Box display="flex" gap={3} padding={3} borderBottomWidth={1} borderBottomStyle="solid" borderColor="default1" alignItems="center">
              <Box __width="200px">
                <Select
                  label="Filter by Platform"
                  name="bannerPlatformFilter"
                  value={bannerPlatformFilter}
                  options={FILTER_PLATFORM_OPTIONS}
                  onChange={({ target }) => setBannerPlatformFilter(target.value as any)}
                />
              </Box>
              <Box __width="200px">
                <Select
                  label="Filter by Device"
                  name="bannerDeviceFilter"
                  value={bannerDeviceFilter}
                  options={FILTER_DEVICE_OPTIONS}
                  onChange={({ target }) => setBannerDeviceFilter(target.value as any)}
                />
              </Box>
            </Box>

            {bannersLoading ? (
              <Box padding={4}><Skeleton /></Box>
            ) : (
              <ResponsiveTable>
                <TableHead>
                  <TableRow>
                    <TableCellHeader>Image</TableCellHeader>
                    <TableCellHeader>Title</TableCellHeader>
                    <TableCellHeader>Targeting</TableCellHeader>
                    <TableCellHeader>Redirect</TableCellHeader>
                    <TableCellHeader>Status</TableCellHeader>
                    <TableCellHeader>Order</TableCellHeader>
                    <TableCellHeader textAlign="right" style={{ width: 140 }}>Actions</TableCellHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(bannersData?.carouselBanners || []).map((b: CarouselBanner) => (
                    <TableRow key={b.id} hover>
                      <TableCell>
                        {b.imageUrl ? (
                          <img
                            src={b.imageUrl}
                            alt={b.title}
                            style={{ width: 80, height: 40, objectFit: "cover", borderRadius: 4 }}
                          />
                        ) : (
                          <Text size={2} color="default2">No Image</Text>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle2">{b.title}</Typography>
                        {b.subtitle && (
                          <Typography variant="caption" color="textSecondary">{b.subtitle}</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={formatTargetingBadge(b.targetPlatform, b.targetDevice)}
                          style={{
                            backgroundColor: "#f1f5f9",
                            color: "#334155",
                            fontWeight: 500,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={`${b.redirectType}: ${b.redirectValue || "None"}`}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={b.isPublished ? "Published" : "Draft"}
                          style={{
                            backgroundColor: b.isPublished ? "#dcfce7" : "#f1f5f9",
                            color: b.isPublished ? "#15803d" : "#475569",
                          }}
                        />
                      </TableCell>
                      <TableCell>{b.sortOrder}</TableCell>
                      <TableCell align="right" style={{ width: 140 }}>
                        <Box display="flex" justifyContent="flex-end" alignItems="center" gap={1}>
                          <IconButton
                            size="small"
                            title="Preview Storefront Banner"
                            onClick={() => setPreviewBanner(b)}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            title="Edit Banner"
                            onClick={() => {
                              setEditingBanner(b);
                              setBannerDialogOpen(true);
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            title="Delete Banner"
                            onClick={() => setDeleteTarget({ id: b.id, name: b.title, type: "Banner" })}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                  {(!bannersData?.carouselBanners || bannersData.carouselBanners.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Box padding={4}><Text>No carousel banners found.</Text></Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </ResponsiveTable>
            )}
          </DashboardCard>
        )}

        {/* ── TAB 1: SHORTS ── */}
        {tabIndex === 1 && (
          <DashboardCard>
            <Box display="flex" gap={3} padding={3} borderBottomWidth={1} borderBottomStyle="solid" borderColor="default1" alignItems="center">
              <Box __width="200px">
                <Select
                  label="Filter by Platform"
                  name="shortPlatformFilter"
                  value={shortPlatformFilter}
                  options={FILTER_PLATFORM_OPTIONS}
                  onChange={({ target }) => setShortPlatformFilter(target.value as any)}
                />
              </Box>
              <Box __width="200px">
                <Select
                  label="Filter by Device"
                  name="shortDeviceFilter"
                  value={shortDeviceFilter}
                  options={FILTER_DEVICE_OPTIONS}
                  onChange={({ target }) => setShortDeviceFilter(target.value as any)}
                />
              </Box>
            </Box>

            {shortsLoading ? (
              <Box padding={4}><Skeleton /></Box>
            ) : (
              <ResponsiveTable>
                <TableHead>
                  <TableRow>
                    <TableCellHeader>Thumbnail</TableCellHeader>
                    <TableCellHeader>Title</TableCellHeader>
                    <TableCellHeader>Targeting</TableCellHeader>
                    <TableCellHeader>Duration</TableCellHeader>
                    <TableCellHeader>Redirect</TableCellHeader>
                    <TableCellHeader>Status</TableCellHeader>
                    <TableCellHeader textAlign="right" style={{ width: 140 }}>Actions</TableCellHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(shortsData?.shortVideos || []).map((v: ShortVideo) => (
                    <TableRow key={v.id} hover>
                      <TableCell>
                        {v.thumbnailUrl ? (
                          <img
                            src={v.thumbnailUrl}
                            alt={v.title}
                            style={{ width: 45, height: 60, objectFit: "cover", borderRadius: 4 }}
                          />
                        ) : (
                          <Text size={2} color="default2">No Cover</Text>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle2">{v.title}</Typography>
                        {v.subtitle && (
                          <Typography variant="caption" color="textSecondary">{v.subtitle}</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={formatTargetingBadge(v.targetPlatform, v.targetDevice)}
                          style={{
                            backgroundColor: "#f1f5f9",
                            color: "#334155",
                            fontWeight: 500,
                          }}
                        />
                      </TableCell>
                      <TableCell>{v.durationSeconds ? `${v.durationSeconds}s` : "—"}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={`${v.redirectType}: ${v.redirectValue || "None"}`}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={v.isPublished ? "Active" : "Hidden"}
                          style={{
                            backgroundColor: v.isPublished ? "#dcfce7" : "#f1f5f9",
                            color: v.isPublished ? "#15803d" : "#475569",
                          }}
                        />
                      </TableCell>
                      <TableCell align="right" style={{ width: 140 }}>
                        <Box display="flex" justifyContent="flex-end" alignItems="center" gap={1}>
                          <IconButton
                            size="small"
                            title="Preview Short Video Reel"
                            onClick={() => setPreviewShort(v)}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            title="Edit Short Video"
                            onClick={() => {
                              setEditingShort(v);
                              setShortDialogOpen(true);
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            title="Delete Short Video"
                            onClick={() => setDeleteTarget({ id: v.id, name: v.title, type: "Short Video" })}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                  {(!shortsData?.shortVideos || shortsData.shortVideos.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Box padding={4}><Text>No short videos uploaded.</Text></Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </ResponsiveTable>
            )}
          </DashboardCard>
        )}

        {/* ── TAB 2: DELIVERY SLOTS ── */}
        {tabIndex === 2 && (
          <DashboardCard>
            {slotsLoading ? (
              <Box padding={4}><Skeleton /></Box>
            ) : (
              <ResponsiveTable>
                <TableHead>
                  <TableRow>
                    <TableCellHeader>Slot Key</TableCellHeader>
                    <TableCellHeader>Label</TableCellHeader>
                    <TableCellHeader>Time Window</TableCellHeader>
                    <TableCellHeader>Day Offset</TableCellHeader>
                    <TableCellHeader>Cutoff</TableCellHeader>
                    <TableCellHeader>Mapped Product Types</TableCellHeader>
                    <TableCellHeader>Status</TableCellHeader>
                    <TableCellHeader textAlign="right" style={{ width: 140 }}>Actions</TableCellHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(slotsData?.cmsDeliverySlots || []).map((s: CMSDeliverySlot) => (
                    <TableRow key={s.id} hover>
                      <TableCell>
                        <Typography variant="body2" style={{ fontFamily: "monospace", fontWeight: 700 }}>
                          {s.slotKey}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle2">{s.label}</Typography>
                        {s.useNextSat && (
                          <Chip size="small" label="Saturday Only" style={{ backgroundColor: "#fef3c7", color: "#b45309" }} />
                        )}
                      </TableCell>
                      <TableCell>{s.timeWindow}</TableCell>
                      <TableCell>{s.dayOffset} day(s)</TableCell>
                      <TableCell>{s.cutoffTime || "None"}</TableCell>
                      <TableCell>
                        <Box display="flex" flexWrap="wrap" gap={1}>
                          {s.productTypes.map(p => (
                            <Chip key={p.id} size="small" label={p.name} />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={s.isActive ? "Active" : "Disabled"}
                          style={{
                            backgroundColor: s.isActive ? "#dcfce7" : "#fee2e2",
                            color: s.isActive ? "#15803d" : "#b91c1c",
                          }}
                        />
                      </TableCell>
                      <TableCell align="right" style={{ width: 140 }}>
                        <Box display="flex" justifyContent="flex-end" alignItems="center" gap={1}>
                          <IconButton
                            size="small"
                            title="Preview Delivery Slot"
                            onClick={() => setPreviewSlot(s)}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            title="Edit Delivery Slot"
                            onClick={() => {
                              setEditingSlot(s);
                              setSlotDialogOpen(true);
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            title="Delete Delivery Slot"
                            onClick={() =>
                              setDeleteTarget({
                                id: s.id,
                                name: `${s.label} (${s.slotKey})`,
                                type: "Delivery Slot",
                              })
                            }
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                  {(!slotsData?.cmsDeliverySlots || slotsData.cmsDeliverySlots.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Box padding={4}><Text>No delivery slots configured.</Text></Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </ResponsiveTable>
            )}
          </DashboardCard>
        )}

        {/* ── TAB 3: VACATIONS ── */}
        {tabIndex === 3 && (
          <DashboardCard>
            {vacationsLoading ? (
              <Box padding={4}><Skeleton /></Box>
            ) : (
              <ResponsiveTable>
                <TableHead>
                  <TableRow>
                    <TableCellHeader>Date</TableCellHeader>
                    <TableCellHeader>Label / Reason</TableCellHeader>
                    <TableCellHeader>Blocked Scope</TableCellHeader>
                    <TableCellHeader textAlign="right" style={{ width: 110 }}>Actions</TableCellHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(vacationsData?.cmsDeliveryVacations || []).map((v: CMSDeliveryVacation) => (
                    <TableRow key={v.id} hover>
                      <TableCell>
                        <Typography variant="body2" style={{ fontWeight: 700 }}>
                          {v.date}
                        </Typography>
                      </TableCell>
                      <TableCell>{v.label || "Blackout Date"}</TableCell>
                      <TableCell>
                        {v.deliverySlots.length === 0 && v.productTypes.length === 0 ? (
                          <Chip size="small" label="GLOBAL (All Slots)" style={{ backgroundColor: "#fee2e2", color: "#b91c1c", fontWeight: 700 }} />
                        ) : (
                          <Box display="flex" flexWrap="wrap" gap={1}>
                            {v.deliverySlots.map(s => (
                              <Chip key={s.id} size="small" label={`Slot: ${s.slotKey}`} />
                            ))}
                            {v.productTypes.map(p => (
                              <Chip key={p.id} size="small" label={`PT: ${p.name}`} />
                            ))}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell align="right" style={{ width: 110 }}>
                        <Box display="flex" justifyContent="flex-end" alignItems="center" gap={1}>
                          <IconButton
                            size="small"
                            onClick={() =>
                              setDeleteTarget({
                                id: v.id,
                                name: v.label ? `${v.date} (${v.label})` : v.date,
                                type: "Vacation Date",
                              })
                            }
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                  {(!vacationsData?.cmsDeliveryVacations || vacationsData.cmsDeliveryVacations.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Box padding={4}><Text>No active blackout dates.</Text></Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </ResponsiveTable>
            )}
          </DashboardCard>
        )}

        {/* ── TAB 4: SEASONAL PICKS ── */}
        {tabIndex === 4 && (
          <DashboardCard>
            {picksLoading ? (
              <Box padding={4}><Skeleton /></Box>
            ) : (
              <ResponsiveTable>
                <TableHead>
                  <TableRow>
                    <TableCellHeader>Produce</TableCellHeader>
                    <TableCellHeader>Season</TableCellHeader>
                    <TableCellHeader>Farmer & Location</TableCellHeader>
                    <TableCellHeader>Tagline</TableCellHeader>
                    <TableCellHeader>Status</TableCellHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(picksData?.seasonalPicks || []).map((p: any) => (
                    <TableRow key={p.id} hover>
                      <TableCell><Typography variant="subtitle2">{p.label}</Typography></TableCell>
                      <TableCell><Chip size="small" label={p.season} /></TableCell>
                      <TableCell>{p.farmerName} ({p.farmLocation})</TableCell>
                      <TableCell>{p.tagline}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={p.isPublished ? "Active" : "Draft"}
                          style={{
                            backgroundColor: p.isPublished ? "#dcfce7" : "#f1f5f9",
                            color: p.isPublished ? "#15803d" : "#475569",
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                  {(!picksData?.seasonalPicks || picksData.seasonalPicks.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Box padding={4}><Text>No seasonal picks configured.</Text></Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </ResponsiveTable>
            )}
          </DashboardCard>
        )}

        {/* ── TAB 5: LOCATIONS ── */}
        {tabIndex === 5 && (
          <DashboardCard>
            {locsLoading ? (
              <Box padding={4}><Skeleton /></Box>
            ) : (
              <ResponsiveTable>
                <TableHead>
                  <TableRow>
                    <TableCellHeader>Location</TableCellHeader>
                    <TableCellHeader>Pincode / City</TableCellHeader>
                    <TableCellHeader>Min Order</TableCellHeader>
                    <TableCellHeader>Delivery Fee</TableCellHeader>
                    <TableCellHeader>Free Above</TableCellHeader>
                    <TableCellHeader>Serviceable</TableCellHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(locsData?.deliveryLocations || []).map((l: any) => (
                    <TableRow key={l.id} hover>
                      <TableCell><Typography variant="subtitle2">{l.name}</Typography></TableCell>
                      <TableCell>{l.pincode}, {l.city}</TableCell>
                      <TableCell>{l.minimumOrderValue ? `₹${l.minimumOrderValue}` : "—"}</TableCell>
                      <TableCell>{l.deliveryFee ? `₹${l.deliveryFee}` : "Free"}</TableCell>
                      <TableCell>{l.freeDeliveryAbove ? `₹${l.freeDeliveryAbove}` : "—"}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={l.isServiceable ? "Serviceable" : "Suspended"}
                          style={{
                            backgroundColor: l.isServiceable ? "#dcfce7" : "#fee2e2",
                            color: l.isServiceable ? "#15803d" : "#b91c1c",
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                  {(!locsData?.deliveryLocations || locsData.deliveryLocations.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Box padding={4}><Text>No delivery locations configured.</Text></Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </ResponsiveTable>
            )}
          </DashboardCard>
        )}
      </Box>

      {/* Modals */}
      <BannerDialog
        key={editingBanner?.id || "new-banner"}
        open={bannerDialogOpen}
        banner={editingBanner}
        onClose={() => {
          setBannerDialogOpen(false);
          setEditingBanner(null);
        }}
      />
      <ShortDialog
        key={editingShort?.id || "new-short"}
        open={shortDialogOpen}
        video={editingShort}
        onClose={() => {
          setShortDialogOpen(false);
          setEditingShort(null);
        }}
      />
      <SlotDialog
        key={editingSlot?.id || "new-slot"}
        open={slotDialogOpen}
        slot={editingSlot}
        onClose={() => {
          setSlotDialogOpen(false);
          setEditingSlot(null);
        }}
      />
      <VacationDialog
        open={vacationDialogOpen}
        onClose={() => setVacationDialogOpen(false)}
      />

      {/* Preview Dialogs */}
      <BannerPreviewDialog
        open={!!previewBanner}
        banner={previewBanner}
        onClose={() => setPreviewBanner(null)}
      />
      <ShortPreviewDialog
        open={!!previewShort}
        short={previewShort}
        onClose={() => setPreviewShort(null)}
      />
      <SlotPreviewDialog
        open={!!previewSlot}
        slot={previewSlot}
        onClose={() => setPreviewSlot(null)}
      />

      {/* Delete Confirmation Popup */}

      <ActionDialog
        open={!!deleteTarget}
        title={`Delete ${deleteTarget?.type || "Item"}?`}
        variant="delete"
        confirmButtonLabel="Delete"
        confirmButtonState={
          deletingBanner || deletingShort || deletingSlot || deletingVacation
            ? "loading"
            : "default"
        }
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
      >
        <Text>
          Are you sure you want to delete <strong>{deleteTarget?.name}</strong>? This action cannot be undone.
        </Text>
      </ActionDialog>
    </ListPageLayout>
  );
};

export default CmsPage;
